import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit'
import axiosInstance from '@utils/axiosInstance'
import { ENTRY_TYPES } from '@constants/entryTypes'
import { SAVE_TYPES } from '@constants/saveTypes'
// import { CONNECTION_SOURCE_TYPES } from '@constants/connectionSourceTypes' // Unused - only used in commented code
// import { deleteConnection } from '@redux/reducers/connectionsReducer' // Unused - only used in commented code

import { showToast } from '@utils/toast'
import { resolvePublicUserId } from '@utils/resolvePublicUserId'
import { normalizeEntryId } from '@utils/normalizeEntryId'
import { getLocalDateKey, getLocalTimeZone } from '@utils/localDateKey'
import { createDeduplicationCondition, clearPendingRequest } from '@utils/requestDeduplication'
import { setJournalWordCountToday } from '@redux/reducers/writingDataReducer'

const { NODE, JOURNAL } = ENTRY_TYPES

/** Dispatched after POST so Explore does not treat a transient null entryId as “pick latest node”. */
export const applyNewJournalDraft = createAction('currentEntryReducer/applyNewJournalDraft')

const initialState = {
  entryId: null,
  entriesLoading: false,
  entriesSaving: false,
  wordCount: 0,
  timeElapsed: 0,
  // TODO the below are temporary until we can effectively consolidate writing data and old style timeElapsed etc
  wdWordCount: 0,
  wdTimeElapsed: 0,
  wpm: 0,
  title: '',
  akas: [],
  shinyTextDismissals: [],
  type: JOURNAL,
  content: '',
  nodeEntriesInfo: [],
  starred: false,
  isTopLevel: false,
  isPrivate: false,
  wordCountGoal: null,
  entryContents: [],
  entryContentsLoading: false,
  globalRenderOwners: {},
}

export const addAka = createAsyncThunk(
  'akas/addAka',
  async ({ entryId, aka }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { akas } = getState().currentEntry
      const akaValues = akas.map((aka) => aka.aka_value)

      const akaExists = akaValues.some((existingAka) => existingAka.toLowerCase() === aka.toLowerCase())

      if (akaExists) {
        dispatch(showToast('Duplicate, AKA not added', 'error'))

        return rejectWithValue({ message: 'Aka already exists' })
      }

      const response = await axiosInstance.post(`api/akas/${entryId}/add_aka`, { aka })
      dispatch(showToast('AKA added', 'success'))

      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const deleteAka = createAsyncThunk(
  'akas/deleteAka',
  async ({ entryId, akaId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.delete(`api/akas/${entryId}/akas/${akaId}`)
      dispatch(showToast('AKA Deleted', 'success'))

      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const setCanonTitleFromAka = createAsyncThunk(
  'akas/setCanonTitleFromAka',
  async ({ entryId, akaId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(`api/akas/${entryId}/set_canon_title`, { akaId })
      dispatch(showToast('Canon title updated', 'success'))
      return response.data
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to set canon title'
      dispatch(showToast(message, 'error'))
      return rejectWithValue(error.response?.data || { message })
    }
  }
)

export const createNodeEntry = createAsyncThunk(
  'currentEntryReducer/createNodeEntry',
  async ({ content = '', title = '' } = {}, { rejectWithValue, dispatch }) => {
    try {
      // Remove undefined values so they don’t get sent
      const payload = {}
      if (content) payload.content = content
      if (title) payload.title = title

      const response = await axiosInstance.post('api/entries/create_node_entry', payload)

      await dispatch(fetchNodeEntriesInfo())

      dispatch(showToast('Node Created', 'success'))

      return response.data.id
    } catch (error) {
      dispatch(showToast('Node creation error', 'error'))
      return rejectWithValue(error.response?.data)
    }
  }
)

export const saveNodeEntry = createAsyncThunk(
  'currentEntryReducer/saveNodeEntry',
  async ({ saveType }, { getState, rejectWithValue, dispatch }) => {
    const { AUTO, MANUAL, EXTERNAL_CONNECTION } = SAVE_TYPES

    try {
      const currentState = await getState().currentEntry
      const fetchResponse = await dispatch(fetchEntryById(currentState.entryId))
      const fetchedEntry = fetchResponse.payload

      const fallbackTitle = `Untitled #${currentState.entryId}`
      const titleToSave = currentState.title?.trim() ? currentState.title.trim() : fetchedEntry?.title?.trim() || fallbackTitle
      const titleChanged = fetchedEntry.title !== titleToSave
      const contentChanged = fetchedEntry.content[0] !== currentState.content

      if (!titleChanged && !contentChanged && saveType !== EXTERNAL_CONNECTION) {
        if (saveType === MANUAL) dispatch(showToast('Nothing to update', 'warn'))

        return currentState
      }

      const response = await axiosInstance.post('api/entries/update_node_entry', {
        entryId: currentState.entryId,
        content: currentState.content,
        title: titleToSave,
      })

      await dispatch(fetchNodeEntriesInfo())

      const cleanedConnections = false

      if (saveType === AUTO) {
        dispatch(showToast(cleanedConnections ? 'Node saved and connections cleaned' : 'Node autosaved', 'warn'))
        return { title: titleToSave }
      } else {
        dispatch(showToast(cleanedConnections ? 'Node autosaved and connections cleaned' : 'Node updated', 'success'))
        return response.data
      }
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const createJournalEntry = createAsyncThunk(
  'currentEntryReducer/createJournalEntry',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('api/entries/create_journal_entry', {
        localDate: getLocalDateKey(),
        timeZone: getLocalTimeZone(),
      })
      const entryId = response.data.entry_id
      const existing = Boolean(response.data.existing)

      if (existing) {
        // Resume today's journal (preserves prior content instead of wiping).
        await dispatch(setEntryById(entryId))
      } else {
        dispatch(applyNewJournalDraft({ entryId }))
      }

      await dispatch(fetchNodeEntriesInfo())

      return entryId
    } catch (error) {
      dispatch(showToast('Journal creation error', 'error'))
      return rejectWithValue(error.response?.data)
    }
  }
)

export const saveJournalEntry = createAsyncThunk(
  'currentEntryReducer/saveJournalEntry',
  async ({ entryId, content, timeElapsed, wpm, wordCount, saveType }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('api/entries/save_journal_entry', {
        content,
        num_of_words: wordCount,
        entryId,
        total_time_taken: timeElapsed,
        wpm,
      })

      if (saveType === SAVE_TYPES.AUTO) {
        dispatch(showToast('Journal autosaved', 'warn'))
      } else {
        dispatch(showToast('Journal Entry Saved', 'success'))
      }

      dispatch(setJournalWordCountToday(wordCount))

      return response.data.entry_id
    } catch (error) {
      dispatch(showToast('Error saving journal entry', 'error'))
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchEntryById = createAsyncThunk(
  'currentEntryReducer/fetchEntryById',
  async (entryId, { rejectWithValue }) => {
    const normalizedEntryId = normalizeEntryId(entryId)
    if (normalizedEntryId == null) {
      return rejectWithValue({ msg: 'Invalid entry ID' })
    }

    try {
      const response = await axiosInstance.get(`api/entries/entry/${normalizedEntryId}`)

      return response.data
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchNodeEntriesInfo = createAsyncThunk(
  'currentEntryReducer/fetchNodeEntriesInfo',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.get('api/entries/node_entries_info')

      return response.data.nodeEntries
    } catch (error) {
      const status = error.response?.status
      // Skip toast for auth failures — user may be logged out or session expired
      if (status !== 401 && status !== 400) {
        dispatch(showToast('Error fetching node entries', 'error'))
      }
      return rejectWithValue({
        ...error.response?.data,
        status,
      })
    }
  }
)

/**
 * Persist the in-memory entry before switching away / creating a new entry.
 * No-ops when there is no entry or nothing worth saving.
 */
export const autosaveCurrentEntryIfNeeded = createAsyncThunk(
  'currentEntryReducer/autosaveCurrentEntryIfNeeded',
  async (_, { getState, dispatch }) => {
    const currentState = getState().currentEntry
    const entryId = normalizeEntryId(currentState.entryId)

    if (entryId == null) {
      return null
    }

    try {
      if (currentState.type === JOURNAL) {
        const content = typeof currentState.content === 'string' ? currentState.content : ''
        if (!content.trim()) {
          return null
        }

        const { timeElapsed = 0, wpm = 0, wordCount = 0 } = currentState
        await dispatch(
          saveJournalEntry({
            entryId,
            content,
            timeElapsed,
            wpm,
            wordCount,
            saveType: SAVE_TYPES.AUTO,
          })
        ).unwrap()
        return entryId
      }

      await dispatch(saveNodeEntry({ saveType: SAVE_TYPES.AUTO })).unwrap()
      return entryId
    } catch (error) {
      console.error('Autosave before entry change failed:', error)
      return null
    }
  }
)

// TODO this is a duplicate of fetchEntryById, Consolidate
export const setEntryById = createAsyncThunk(
  'currentEntryReducer/setEntryById',
  async (queryParamEntryId, { getState, dispatch, rejectWithValue }) => {
    const normalizedEntryId = normalizeEntryId(queryParamEntryId)
    if (normalizedEntryId == null) {
      return rejectWithValue({ msg: 'Invalid entry ID' })
    }

    const previousEntryId = normalizeEntryId(getState().currentEntry.entryId)
    if (previousEntryId != null && previousEntryId !== normalizedEntryId) {
      await dispatch(autosaveCurrentEntryIfNeeded())
    }

    try {
      const response = await axiosInstance.get(`api/entries/entry/${normalizedEntryId}`)
      const {
        content,
        connections,
        date,
        id: entryId,
        num_of_words: wordCount,
        starred,
        title,
        wdWordCount,
        wdTimeElapsed,
        isTopLevel,
        is_top_level: isTopLevelSnake,
        is_private: isPrivate,
        word_count_goal: wordCountGoal,
        type: entryType,
      } = response.data

      return {
        wdWordCount,
        wdTimeElapsed,
        content: content && content[0] != null ? content[0] : '',
        connections,
        date,
        entryId,
        wordCount,
        starred,
        title: title ?? '',
        isTopLevel: Boolean(isTopLevel ?? isTopLevelSnake),
        isPrivate: isPrivate || false,
        wordCountGoal: wordCountGoal != null ? Number(wordCountGoal) : null,
        type: entryType === JOURNAL ? JOURNAL : NODE,
      }
    } catch (error) {
      return rejectWithValue(error.response.data)
    }
  }
)

export const fetchAkas = createAsyncThunk('akas/fetchAkas', async (entryId, { rejectWithValue }) => {
  const normalizedEntryId = normalizeEntryId(entryId)
  if (normalizedEntryId == null) {
    return rejectWithValue({ msg: 'Invalid entry ID' })
  }

  try {
    const response = await axiosInstance.get(`api/akas/${normalizedEntryId}/akas`)
    return response.data.akas
  } catch (error) {
    return rejectWithValue(error.response.data)
  }
})

export const fetchShinyTextDismissals = createAsyncThunk(
  'shinyText/fetchDismissals',
  async (entryId, { rejectWithValue }) => {
    const normalizedEntryId = normalizeEntryId(entryId)
    if (normalizedEntryId == null) {
      return rejectWithValue({ msg: 'Invalid entry ID' })
    }

    try {
      const response = await axiosInstance.get(`api/shiny_text_suggestions/${normalizedEntryId}/dismissals`)
      return response.data.dismissals
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error)
    }
  }
)

export const dismissShinyTextSuggestion = createAsyncThunk(
  'shinyText/dismissSuggestion',
  async ({ entryId, suggestedEntryId }, { rejectWithValue }) => {
    const normalizedEntryId = normalizeEntryId(entryId)
    const normalizedSuggestedEntryId = normalizeEntryId(suggestedEntryId)
    if (normalizedEntryId == null || normalizedSuggestedEntryId == null) {
      return rejectWithValue({ msg: 'Invalid entry ID' })
    }

    try {
      const response = await axiosInstance.post(`api/shiny_text_suggestions/${normalizedEntryId}/dismissals`, {
        suggested_entry_id: normalizedSuggestedEntryId,
      })
      return response.data.dismissal
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error)
    }
  }
)

export const deleteEntry = createAsyncThunk(
  'currentEntryReducer/deleteEntry',
  async (entryId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.delete(`api/entries/delete_entry/${entryId}`)
      dispatch(showToast('Entry Deleted', 'success'))
      return response.data
    } catch (error) {
      dispatch(showToast('Error deleting entry', 'error'))
      return rejectWithValue(error.response.data)
    }
  }
)

export const toggleNodeStarred = createAsyncThunk(
  'currentEntryReducer/toggleNodeStarred',
  async ({ entryId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('api/entries/toggle_starred', { entryId })

      await dispatch(fetchNodeEntriesInfo())

      return response.data
    } catch (error) {
      dispatch(showToast('Error updating starred status', 'error'))
      return rejectWithValue(error.response.data)
    }
  }
)

export const updateNodeTopLevel = createAsyncThunk(
  'currentEntryReducer/updateNodeTopLevel',
  async ({ entryId, isTopLevel }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('api/entries/update_node_top_level', {
        entryId,
        isTopLevel,
      })

      await dispatch(fetchNodeEntriesInfo())

      return response.data
    } catch (error) {
      dispatch(showToast('Error updating top level status', 'error'))
      return rejectWithValue(error.response.data)
    }
  }
)

export const toggleEntryIsPrivate = createAsyncThunk(
  'currentEntryReducer/toggleEntryIsPrivate',
  async ({ entryId }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('api/entries/toggle_is_private', { entryId })

      await dispatch(fetchNodeEntriesInfo())

      return response.data
    } catch (error) {
      dispatch(showToast('Error updating privacy status', 'error'))
      return rejectWithValue(error.response.data)
    }
  }
)

export const updateWordCountGoal = createAsyncThunk(
  'currentEntryReducer/updateWordCountGoal',
  async ({ entryId, wordCountGoal }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post('api/entries/update_word_count_goal', {
        entryId,
        wordCountGoal,
      })

      dispatch(showToast(wordCountGoal ? 'Node word goal saved' : 'Node word goal cleared', 'success'))
      return response.data
    } catch (error) {
      dispatch(showToast('Error updating word count goal', 'error'))
      return rejectWithValue(error.response?.data ?? error)
    }
  }
)

export const fetchPublicEntry = createAsyncThunk(
  'currentEntryReducer/fetchPublicEntry',
  async ({ entryId, userId }, { rejectWithValue }) => {
    const arg = { entryId, userId }
    try {
      const resolvedUserId = resolvePublicUserId(userId)
      const response = await fetch(`/api/entries/public/entry/${entryId}?userId=${resolvedUserId}`)
      if (!response.ok) {
        throw new Error('Failed to fetch entry')
      }
      const data = await response.json()
      return data
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to fetch entry' })
    } finally {
      clearPendingRequest('currentEntryReducer/fetchPublicEntry', arg)
    }
  },
  {
    condition: createDeduplicationCondition('currentEntryReducer/fetchPublicEntry'),
  }
)

export const fetchPublicNodeEntriesInfo = createAsyncThunk(
  'currentEntryReducer/fetchPublicNodeEntriesInfo',
  async (userId, { rejectWithValue }) => {
    try {
      const resolvedUserId = resolvePublicUserId(userId)
      const response = await fetch(`/api/entries/public/node_entries_info/${resolvedUserId}`)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ msg: 'Failed to fetch nodes' }))
        throw new Error(errorData.msg || 'Failed to fetch nodes')
      }
      const data = await response.json()
      return data.nodeEntries || []
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to fetch nodes' })
    } finally {
      clearPendingRequest('currentEntryReducer/fetchPublicNodeEntriesInfo', userId)
    }
  },
  {
    condition: createDeduplicationCondition('currentEntryReducer/fetchPublicNodeEntriesInfo'),
  }
)

export const fetchPublicEntryContents = createAsyncThunk(
  'currentEntryReducer/fetchPublicEntryContents',
  async ({ entryId, userId }, { rejectWithValue }) => {
    const arg = { entryId, userId }
    try {
      const resolvedUserId = resolvePublicUserId(userId)
      const response = await fetch(`/api/entries/public/entry_contents/${entryId}?userId=${resolvedUserId}`)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch entry contents' }))
        throw new Error(errorData.message || 'Failed to fetch entry contents')
      }
      const data = await response.json()
      return data.contents || []
    } catch (error) {
      return rejectWithValue({ message: error.message || 'Failed to fetch entry contents' })
    } finally {
      clearPendingRequest('currentEntryReducer/fetchPublicEntryContents', arg)
    }
  },
  {
    condition: createDeduplicationCondition('currentEntryReducer/fetchPublicEntryContents'),
  }
)

const currentEntrySlice = createSlice({
  name: 'currentEntryReducer',
  initialState,
  reducers: {
    setAkas: (state, action) => {
      state.akas = action.payload
    },
    setWordCount: (state, action) => {
      state.wordCount = action.payload
    },
    setEntryId: (state, action) => {
      state.entryId = action.payload
    },
    setCharCount: (state, action) => {
      state.charCount = action.payload
    },
    setTitle: (state, action) => {
      state.title = action.payload
    },
    setTitleAndResetAll: (state, action) => {
      Object.assign(state, { ...initialState, title: action.payload })
    },
    setTimeElapsed(state, action) {
      state.timeElapsed = action.payload
    },
    setConnections: (state, action) => {
      state.connections = action.payload
    },
    setTypeNode: (state) => {
      state.type = NODE
    },
    setTypeJournal: (state) => {
      state.type = JOURNAL
    },
    setContent: (state, action) => {
      state.content = action.payload
    },
    setWPM(state, action) {
      state.wpm = action.payload
    },
    setGlobalRenderOwners: (state, action) => {
      state.globalRenderOwners = action.payload || {}
    },
    claimGlobalRenderOwners: (state, action) => {
      const { ownerId, nodeIds } = action.payload || {}
      if (!ownerId || !nodeIds?.length) return
      nodeIds.forEach((nodeId) => {
        if (!state.globalRenderOwners[nodeId]) {
          state.globalRenderOwners[nodeId] = ownerId
        }
      })
    },
    resetCurrentEntryState: () => initialState,
    resetJournalEntryDraft: (state) => ({
      ...initialState,
      nodeEntriesInfo: state.nodeEntriesInfo,
      type: JOURNAL,
      entryId: state.entryId,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(applyNewJournalDraft, (state, action) => {
        const { entryId } = action.payload
        state.entryId = entryId
        state.content = ''
        state.title = ''
        state.type = JOURNAL
        state.wordCount = 0
        state.timeElapsed = 0
        state.wpm = 0
        state.wdWordCount = 0
        state.wdTimeElapsed = 0
        state.starred = false
        state.isTopLevel = false
        state.isPrivate = false
        state.wordCountGoal = null
        state.akas = []
        state.shinyTextDismissals = []
        state.entryContents = []
        state.globalRenderOwners = {}
        delete state.connections
        delete state.date
      })
      .addCase(fetchAkas.fulfilled, (state, action) => {
        state.akas = action.payload
      })
      .addCase(fetchShinyTextDismissals.fulfilled, (state, action) => {
        state.shinyTextDismissals = action.payload
      })
      .addCase(dismissShinyTextSuggestion.pending, (state, action) => {
        const { entryId, suggestedEntryId } = action.meta.arg
        const normalizedSuggestedEntryId = Number(suggestedEntryId)
        const alreadyDismissed = state.shinyTextDismissals.some(
          (dismissal) => Number(dismissal.suggested_entry_id) === normalizedSuggestedEntryId
        )
        if (!alreadyDismissed) {
          state.shinyTextDismissals.push({
            entry_id: Number(entryId),
            suggested_entry_id: normalizedSuggestedEntryId,
          })
        }
      })
      .addCase(dismissShinyTextSuggestion.fulfilled, (state, action) => {
        const dismissal = action.payload
        const index = state.shinyTextDismissals.findIndex(
          (existing) => Number(existing.suggested_entry_id) === Number(dismissal.suggested_entry_id)
        )
        if (index === -1) {
          state.shinyTextDismissals.push(dismissal)
        } else {
          state.shinyTextDismissals[index] = dismissal
        }
      })
      .addCase(dismissShinyTextSuggestion.rejected, (state, action) => {
        const { suggestedEntryId } = action.meta.arg
        const normalizedSuggestedEntryId = Number(suggestedEntryId)
        state.shinyTextDismissals = state.shinyTextDismissals.filter(
          (dismissal) => Number(dismissal.suggested_entry_id) !== normalizedSuggestedEntryId || dismissal.id != null
        )
      })
      .addCase(addAka.fulfilled, (state, action) => {
        state.akas = [...state.akas, action.payload.aka]
      })
      .addCase(deleteAka.fulfilled, (state, action) => {
        state.akas = action.payload.akas
      })
      .addCase(setCanonTitleFromAka.fulfilled, (state, action) => {
        state.title = action.payload.title
        state.akas = action.payload.akas
      })
      .addCase(createNodeEntry.pending, (state) => {
        state.entriesLoading = true
      })
      .addCase(createNodeEntry.fulfilled, (state) => {
        state.entriesLoading = false
      })
      .addCase(createNodeEntry.rejected, (state) => {
        state.entriesLoading = false
      })
      .addCase(createJournalEntry.pending, (state) => {
        state.entriesLoading = true
      })
      .addCase(createJournalEntry.fulfilled, (state) => {
        // Content/entryId are already set by applyNewJournalDraft (new) or setEntryById (resume).
        state.entriesLoading = false
        state.type = JOURNAL
      })
      .addCase(createJournalEntry.rejected, (state) => {
        state.entriesLoading = false
        state.type = JOURNAL
      })
      .addCase(saveJournalEntry.pending, (state) => {
        state.entriesLoading = true
      })
      .addCase(saveJournalEntry.fulfilled, (state, action) => {
        return {
          ...state,
          entriesLoading: false,
          entryId: action.payload,
        }
      })
      .addCase(saveNodeEntry.pending, (state, action) => {
        state.entriesLoading = true
        // Only lock the editor for explicit manual saves. Autosave (title blur, idle,
        // connection prep) used to flip readOnly + overlay and block paste mid-typing.
        const saveType = action.meta?.arg?.saveType
        if (saveType === SAVE_TYPES.MANUAL) {
          state.entriesSaving = true
        }
      })
      .addCase(saveNodeEntry.fulfilled, (state, action) => {
        state.entriesLoading = false
        state.entriesSaving = false

        const payload = action.payload
        if (!payload || typeof payload !== 'object') {
          return
        }

        if (payload.content) {
          state.content = payload.content
        }

        const nextTitle = payload.updatedEntry?.title || payload.title
        if (typeof nextTitle === 'string' && nextTitle.trim()) {
          state.title = nextTitle
        }
      })
      .addCase(saveNodeEntry.rejected, (state) => {
        state.entriesLoading = false
        state.entriesSaving = false
      })
      .addCase(fetchEntryById.pending, (state) => {
        return {
          ...state,
          entriesLoading: true,
        }
      })
      .addCase(fetchEntryById.fulfilled, (state) => {
        return {
          ...state,
          entriesLoading: false,
        }
      })
      .addCase(fetchEntryById.rejected, (state) => {
        return {
          ...state,
          entriesLoading: false,
        }
      })
      .addCase(setEntryById.pending, (state) => {
        return {
          ...state,
          entriesLoading: true,
        }
      })
      .addCase(setEntryById.fulfilled, (state, action) => {
        return {
          ...state,
          ...action.payload,
          shinyTextDismissals: [],
          entriesLoading: false,
        }
      })
      .addCase(setEntryById.rejected, (state) => {
        return {
          ...state,
          entriesLoading: false,
        }
      })
      .addCase(fetchNodeEntriesInfo.pending, (state) => {
        return {
          ...state,
          entriesLoading: true,
        }
      })
      .addCase(fetchNodeEntriesInfo.fulfilled, (state, action) => {
        return {
          ...state,
          nodeEntriesInfo: action.payload,
          entriesLoading: false,
        }
      })
      .addCase(fetchNodeEntriesInfo.rejected, (state) => {
        return {
          ...state,
          entriesLoading: false,
        }
      })
      .addCase(deleteEntry.pending, (state) => {
        return {
          ...state,
          entriesLoading: true,
        }
      })
      .addCase(deleteEntry.fulfilled, (state) => {
        return {
          ...state,
          entriesLoading: false,
        }
      })
      .addCase(toggleNodeStarred.fulfilled, (state, action) => {
        const { entryId, starred } = action.payload
        if (state.entryId === entryId) {
          state.starred = starred
        }
      })
      .addCase(updateNodeTopLevel.fulfilled, (state, action) => {
        const { entryId, isTopLevel } = action.payload
        if (state.entryId === entryId) {
          state.isTopLevel = isTopLevel
        }
      })
      .addCase(toggleEntryIsPrivate.fulfilled, (state, action) => {
        const { entryId, isPrivate } = action.payload
        if (state.entryId === entryId) {
          state.isPrivate = isPrivate
        }
      })
      .addCase(updateWordCountGoal.fulfilled, (state, action) => {
        const { entryId, wordCountGoal } = action.payload
        if (state.entryId === entryId) {
          state.wordCountGoal = wordCountGoal != null ? Number(wordCountGoal) : null
        }
      })
      .addCase(fetchPublicEntry.pending, (state, action) => {
        const requestedEntryId = action.meta.arg?.entryId
        // Only set loading if we're fetching a different entry than what's currently loaded
        const needsLoading = !state.entryId || state.entryId !== requestedEntryId || !state.title || !state.content
        // Clear entryContents if we're switching to a different entry
        const switchingEntries = state.entryId && state.entryId !== requestedEntryId

        state.entriesLoading = needsLoading
        // Clear if loading new entry or switching entries
        if (needsLoading || switchingEntries) {
          state.entryContents = []
        }
        // Note: nodeEntriesInfo is cleared in fetchPublicNodeEntriesInfo.pending when userId changes
        // This prevents memory accumulation when switching between users
      })
      .addCase(fetchPublicEntry.fulfilled, (state, action) => {
        const { content, id: entryId, num_of_words: wordCount, title } = action.payload

        return {
          ...state,
          entryId,
          title,
          content: content && content[0] ? content[0] : '',
          wordCount,
          entriesLoading: false,
          type: NODE,
        }
      })
      .addCase(fetchPublicEntry.rejected, (state) => {
        return {
          ...state,
          entriesLoading: false,
        }
      })
      .addCase(fetchPublicNodeEntriesInfo.pending, (state) => {
        // Clear existing nodeEntriesInfo to prevent memory accumulation when switching users
        // This ensures we don't keep data from previous users in memory
        return {
          ...state,
          nodeEntriesInfo: [], // Clear before fetching new data
          entriesLoading: true,
        }
      })
      .addCase(fetchPublicNodeEntriesInfo.fulfilled, (state, action) => {
        return {
          ...state,
          nodeEntriesInfo: action.payload,
          entriesLoading: false,
        }
      })
      .addCase(fetchPublicNodeEntriesInfo.rejected, (state) => {
        return {
          ...state,
          entriesLoading: false,
        }
      })
      .addCase(fetchPublicEntryContents.pending, (state) => {
        state.entryContentsLoading = true
      })
      .addCase(fetchPublicEntryContents.fulfilled, (state, action) => {
        state.entryContents = action.payload
        state.entryContentsLoading = false
      })
      .addCase(fetchPublicEntryContents.rejected, (state) => {
        state.entryContentsLoading = false
        state.entryContents = []
      })
  },
})

export const {
  resetCurrentEntryState,
  resetJournalEntryDraft,
  setAkas,
  setEntryId,
  setWordCount,
  setCharCount,
  setTitle,
  setTitleAndResetAll,
  setTimeElapsed,
  setConnections,
  setTypeNode,
  setTypeJournal,
  setContent,
  setWPM,
  setGlobalRenderOwners,
  claimGlobalRenderOwners,
} = currentEntrySlice.actions

export default currentEntrySlice.reducer
